// BookingList.jsx  (POS-styled, JSX-only)

import React, { useMemo, useState, useEffect } from "react";
import { LogIn, LogOut, RotateCcw, Users } from "react-feather";
import AddArrivals from "../../components/modals/CheckInOut/Arrivals.jsx";
import AddDepartures from "../../components/modals/CheckInOut/Departures.jsx";
import ListInvoiceModal from "../../components/modals/CheckInOut/ListInvoiceModal.jsx";
import { apiService } from "../../../services/apiService.ts";
import { Folder } from "feather-icons-react/build/IconComponents";
import { Link } from 'react-router-dom'

export default function BackOdHouse() {
    // SEARCH / SORT / PAGINATION
    const [searchText, setSearchText] = useState("");
    const [sortField, setSortField] = useState("BookingHeaderID");
    const [sortOrder, setSortOrder] = useState("asc"); // 'asc' | 'desc'
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // ARRIVALS MODAL
    const [isArrivalsModalOpen, setIsArrivalsModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);

    // DEPARTURES MODAL
    const [isDeparturesModalOpen, setIsDeparturesModalOpen] = useState(false);
    const [departuresBooking, setDeparturesBooking] = useState(null);
    const [departuresAccount, setDeparturesAccount] = useState(null);

    // INVOICES MODAL
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [invoiceAccount, setInvoiceAccount] = useState(null);

    // ACCOUNTS SIDE CARD
    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [accountsError, setAccountsError] = useState(null);

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [showAccountsGrid, setShowAccountsGrid] = useState(false);

    const handleSearchChange = (e) => {
        setSearchText(e.target.value || "");
        setCurrentPage(1);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const goToPage = (page) => {
        if (page < 1) return;
        setCurrentPage(page);
    };

    const handleChangePageSize = (e) => {
        const val = Number(e.target.value) || 10;
        setItemsPerPage(val);
        setCurrentPage(1);
    };

    // ---------------------------------------------
    // LOAD ACCOUNTS FOR BOOKING
    // ---------------------------------------------
    const handleViewAccounts = async (booking) => {
        if (!booking) return;

        setDeparturesBooking(booking);
        setSelectedBooking(booking);
        setLoadingAccounts(true);
        setAccountsError(null);
        setAccounts([]);

        try {
            const res = await apiService.getBookingAccounts(
                booking.BookingHeaderID
            );

            if (res && res.Success && Array.isArray(res.Data)) {
                setAccounts(res.Data);
            } else {
                setAccounts([]);
                setAccountsError("Unable to load accounts for this booking.");
            }
        } catch (err) {
            console.error("Error loading accounts:", err);
            setAccounts([]);
            setAccountsError("Error loading accounts.");
        } finally {
            setLoadingAccounts(false);
        }
    };

    // ---------------------------------------------
    // ARRIVALS
    // ---------------------------------------------
    const openArrivals = (booking) => {
        setSelectedBooking(booking);
        setIsArrivalsModalOpen(true);
    };

    // ---------------------------------------------
    // DEPARTURES (needs account)
    // ---------------------------------------------
    const openDeparturesForAccount = (account) => {
        setDeparturesAccount(account);
        setIsDeparturesModalOpen(true);
    };

    // ---------------------------------------------
    // INVOICES (needs account)
    // ---------------------------------------------
    const openInvoicesForAccount = (account) => {
        setInvoiceAccount(account);
        setIsInvoiceModalOpen(true);
    };

    // ---------------------------------------------
    // FILTER + SORT + PAGINATE LIST
    // ---------------------------------------------
    const processedList = useMemo(() => {
        // 1) Filter
        const filtered = (bookings || []).filter((b) => {
            if (!searchText) return true;
            const text = searchText.toLowerCase();

            const party =
                (b.PartyName ||
                    b.GuestName ||
                    b.CustomerName ||
                    "").toString().toLowerCase();

            const bookingRef =
                (b.BookingReference ||
                    b.ReferenceNo ||
                    b.BookingRef ||
                    "").toString().toLowerCase();

            const room =
                (b.RoomName || b.UnitName || b.Room || "").toString().toLowerCase();

            return (
                party.includes(text) ||
                bookingRef.includes(text) ||
                room.includes(text)
            );
        });

        // 2) Sort
        const sorted = filtered.slice().sort((a, b) => {
            const valA = a[sortField];
            const valB = b[sortField];

            if (valA == null && valB == null) return 0;
            if (valA == null) return sortOrder === "asc" ? -1 : 1;
            if (valB == null) return sortOrder === "asc" ? 1 : -1;

            if (typeof valA === "number" && typeof valB === "number") {
                return sortOrder === "asc" ? valA - valB : valB - valA;
            }

            const strA = String(valA);
            const strB = String(valB);
            return sortOrder === "asc"
                ? strA.localeCompare(strB)
                : strB.localeCompare(strA);
        });

        return sorted;
    }, [bookings, searchText, sortField, sortOrder]);

    const processedAccounts = useMemo(() => {
        if (!showAccountsGrid) return accounts;

        if (!searchText) return accounts;

        const text = searchText.toLowerCase();

        return accounts.filter((a) => {
            return (
                (a.Name || "").toLowerCase().includes(text) ||
                String(a.FK_BookingHeaderID || "").toLowerCase().includes(text)
            );
        });
    }, [accounts, searchText, showAccountsGrid]);


    // Pagination
    const totalItems = processedList.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const safeCurrentPage =
        currentPage > totalPages ? totalPages : currentPage;
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const pagedList = processedList.slice(
        startIndex,
        startIndex + itemsPerPage
    );




    useEffect(() => {
        async function load() {
            try {
                setLoading(true);
                setLoadError(null);

                const res = await apiService.getCheckInBookings();

                if (res && res.Success && Array.isArray(res.Data)) {
                    setBookings(res.Data);
                } else {
                    console.error("Unexpected bookings response:", res);
                    setLoadError("Unable to load bookings.");
                }
            } catch (err) {
                console.error("Booking load error:", err);
                setLoadError("Failed to load bookings.");
            }

            setLoading(false);
        }

        load();
    }, []);


    // ---------------------------------------------
    // RENDER
    // ---------------------------------------------
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* PAGE HEADER */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4> {!showAccountsGrid
                                ? "Bookings"
                                : "Accounts"
                            }</h4>
                            <h6>
                                {!showAccountsGrid
                                    ? "Manage Arrivals & Departures"
                                    : `Accounts for ${selectedBooking?.PartyName || selectedBooking?.GuestName || selectedBooking?.BookingReference || ""}`
                                }
                            </h6>

                        </div>
                    </div>

                    {/* Right icons – PDF / Excel / Print / Refresh / Collapse */}
                    <ul className="table-top-head">
                        <li>
                            <button className="btn btn-link p-0" type="button">
                                <i className="feather feather-file-text" />
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-link p-0" type="button">
                                <i className="feather feather-file" />
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-link p-0" type="button">
                                <i className="feather feather-printer" />
                            </button>
                        </li>
                        <li>
                            <button
                                className="btn btn-link p-0"
                                type="button"
                                onClick={() => {
                                    // simple "refresh" – in future could call API again
                                    setSearchText("");
                                    setSortField("BookingHeaderID");
                                    setSortOrder("asc");
                                    setCurrentPage(1);
                                }}
                            >
                                <RotateCcw />
                            </button>
                        </li>
                    </ul>
                </div>

                {/* MAIN CARD */}
                {!showAccountsGrid && (
                <div className="card table-list-card">
                    <div className="card-body">
                        {/* TABLE TOP – SEARCH & FILTER & SORT */}
                        {!showAccountsGrid && (
                            <div className="table-top">
                                <div className="search-set">
                                    <div className="search-input">
                                        <input
                                            type="text"
                                            placeholder="Search bookings..."
                                            className="form-control form-control-sm formsearch"
                                            value={searchText}
                                            onChange={handleSearchChange}
                                        />
                                        <button className="btn btn-searchset" type="button">
                                            <i className="feather-search" />
                                        </button>
                                    </div>
                                </div>

                                <div
                                    className="form-sort d-flex align-items-center"
                                    style={{
                                        background: "#f7f8fc",
                                        borderRadius: "8px",
                                        padding: "6px 10px",
                                        gap: "6px",
                                        border: "1px solid #e5e7eb",
                                    }}
                                >
                                    <select
                                        className="form-select form-select-sm"
                                        value={itemsPerPage}
                                        onChange={handleChangePageSize}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            fontWeight: 500,
                                            padding: "2px 4px",
                                            width: "90px",
                                            boxShadow: "none",
                                        }}
                                    >
                                        <option value={10}>10 / page</option>
                                        <option value={25}>25 / page</option>
                                        <option value={50}>50 / page</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {!showAccountsGrid && (
                            <>
                                {/* BOOKINGS TABLE */}
                                <div className="table-responsive">
                                    <table className="table datanew">
                                        <thead>
                                            <tr>
                                                <th
                                                    onClick={() => handleSort("BookingHeaderID")}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    Ref
                                                    {sortField === "BookingHeaderID" &&
                                                        (sortOrder === "asc" ? " ▲" : " ▼")}
                                                </th>
                                                <th>Name</th>
                                                <th>Arrival</th>
                                                <th>Departure</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagedList.map((b) => {
                                                const ref =
                                                    b.BookingReference ||
                                                    b.ReferenceNo ||
                                                    b.BookingHeaderID;

                                                const party =
                                                    b.PartyName || b.GuestName || "—";

                                                return (
                                                    <tr key={b.BookingHeaderID}>
                                                        <td>{ref}</td>
                                                        <td>{party}</td>
                                                        <td>{b.TravelStart?.split("T")[0]}</td>
                                                        <td>{b.TravelEnd?.split("T")[0]}</td>
                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-center gap-1">
                                                                {/* Arrivals / Check-in */}
                                                                <Link
                                                                    to="#"
                                                                    className="pos-action-btn btn-success"
                                                                    title="Check In"
                                                                    onClick={() => openArrivals(b)}
                                                                >
                                                                    <LogIn className="feather-16 me-1" />

                                                                </Link>

                                                                <Link
                                                                    to="#"
                                                                    className="pos-action-btn btn-primary"
                                                                    title="View Accounts"
                                                                    onClick={() => {
                                                                        handleViewAccounts(b);
                                                                        setShowAccountsGrid(true);
                                                                    }}
                                                                >
                                                                    <Users className="feather-16 me-1" />

                                                                </Link>

                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            {pagedList.length === 0 && (
                                                <tr>
                                                    {loading && (
                                                        <tr>
                                                            <td colSpan={8} className="text-center py-4 text-muted">
                                                                Loading bookings…
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {loadError && !loading && (
                                                        <tr>
                                                            <td colSpan={8} className="text-center py-4 text-danger">
                                                                {loadError}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* PAGINATION */}
                                <div
                                    className="mt-3"
                                    style={{
                                        background: "#f7f8fc",
                                        borderRadius: "8px",
                                        padding: "14px 20px",
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center">

                                        {/* LEFT SIDE: Showing text */}
                                        <div
                                            className="text-muted small"
                                            style={{ fontWeight: 500 }}
                                        >
                                            Showing {startIndex + 1}–
                                            {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems}
                                        </div>

                                        {/* PAGINATION */}
                                        <div className="d-flex align-items-center gap-2">

                                            {/* PREVIOUS BUTTON */}
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                style={{
                                                    borderRadius: "8px",
                                                    padding: "4px 14px",
                                                    fontWeight: 500,
                                                }}
                                                disabled={safeCurrentPage === 1}
                                                onClick={() => goToPage(safeCurrentPage - 1)}
                                            >
                                                Previous
                                            </button>

                                            {/* CURRENT PAGE BADGE */}
                                            <div
                                                className="px-3 py-1 text-white"
                                                style={{
                                                    background: "#0ea5e9",
                                                    borderRadius: "8px",
                                                    fontWeight: 600,
                                                    fontSize: "0.9rem",
                                                }}
                                            >
                                                {safeCurrentPage}
                                            </div>

                                            {/* NEXT BUTTON */}
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                style={{
                                                    borderRadius: "8px",
                                                    padding: "4px 14px",
                                                    fontWeight: 500,
                                                }}
                                                disabled={safeCurrentPage === totalPages}
                                                onClick={() => goToPage(safeCurrentPage + 1)}
                                            >
                                                Next
                                            </button>

                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                )}
                {/* ACCOUNTS CARD (RIGHT / BELOW) */}
               {showAccountsGrid && selectedBooking && (
    <div className="card table-list-card mt-3">
        <div className="card-body">

            {/* FILTER BAR (same as bookings) */}
            <div className="table-top d-flex justify-content-between align-items-center">

                {/* SEARCH */}
                <div className="search-set">
                    <div className="search-input">
                        <input
                            type="text"
                            placeholder="Search accounts..."
                            className="form-control form-control-sm formsearch"
                            value={searchText}
                            onChange={handleSearchChange}
                        />
                        <button className="btn btn-searchset" type="button">
                            <i className="feather-search" />
                        </button>
                    </div>
                </div>

                {/* PAGE SIZE + CLOSE BUTTON */}
                <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                    <div
                        className="form-sort d-flex align-items-center"
                        style={{
                            background: "#f7f8fc",
                            borderRadius: "8px",
                            padding: "6px 10px",
                            gap: "6px",
                            border: "1px solid #e5e7eb"
                        }}
                    >
                        <select
                            className="form-select form-select-sm"
                            value={itemsPerPage}
                            onChange={handleChangePageSize}
                            style={{
                                border: "none",
                                background: "transparent",
                                fontWeight: 500,
                                padding: "2px 4px",
                                width: "90px",
                                boxShadow: "none",
                            }}
                        >
                            <option value={10}>10 / page</option>
                            <option value={25}>25 / page</option>
                            <option value={50}>50 / page</option>
                        </select>
                    </div>

                    {/* MODAL-STYLE CLOSE BUTTON */}
                    <button
                        className="btn-close"
                        onClick={() => {
                            setSelectedBooking(null);
                            setAccounts([]);
                            setAccountsError(null);
                            setShowAccountsGrid(false);
                        }}
                    />
                </div>
            </div>

            {/* ACCOUNTS TABLE (styled like bookings table) */}
            <div className="table-responsive mt-3">
                <table className="table datanew">
                    <thead>
                        <tr>
                            <th>Account Name</th>
                            <th>Booking Link</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accounts.map((acc) => (
                            <tr key={acc.AccountID}>
                                <td>{acc.Name}</td>
                                <td>{acc.FK_BookingHeaderID}</td>
                                <td className="text-center">
                                    <div className="d-flex justify-content-center gap-1">
                                        <Link
                                            to="#"
                                            className="pos-action-btn btn-warning"
                                            title="Check Out"
                                            onClick={() => openDeparturesForAccount(acc)}
                                        >
                                            <LogOut className="feather-16 me-1" />
                                        </Link>

                                        <Link
                                            to="#"
                                            className="pos-action-btn btn-dark"
                                            title="Invoices"
                                            onClick={() => openInvoicesForAccount(acc)}
                                        >
                                            <Folder className="feather-16 me-1" />
                                        </Link>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* PAGINATION (identical to bookings) */}
            <div
                className="mt-3"
                style={{
                    background: "#f7f8fc",
                    borderRadius: "8px",
                    padding: "14px 20px",
                }}
            >
                <div className="d-flex justify-content-between align-items-center">

                    <div className="text-muted small" style={{ fontWeight: 500 }}>
                        Showing {startIndex + 1}–
                        {Math.min(startIndex + itemsPerPage, accounts.length)} of {accounts.length}
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={safeCurrentPage === 1}
                            onClick={() => goToPage(safeCurrentPage - 1)}
                        >
                            Previous
                        </button>

                        <div
                            className="px-3 py-1 text-white"
                            style={{
                                background: "#0ea5e9",
                                borderRadius: "8px",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                            }}
                        >
                            {safeCurrentPage}
                        </div>

                        <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={safeCurrentPage === totalPages}
                            onClick={() => goToPage(safeCurrentPage + 1)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
)}

            </div>

            {/* MODALS */}
            {isArrivalsModalOpen && selectedBooking && (
                <AddArrivals
                    isOpen={isArrivalsModalOpen}
                    onClose={() => setIsArrivalsModalOpen(false)}
                    booking={selectedBooking}
                />
            )}

            {isDeparturesModalOpen && departuresBooking && departuresAccount && (
                <AddDepartures
                    isOpen={isDeparturesModalOpen}
                    onClose={() => setIsDeparturesModalOpen(false)}
                    booking={departuresBooking}
                    initialAccount={departuresAccount}
                />
            )}

            {isInvoiceModalOpen && invoiceAccount && (
                <ListInvoiceModal
                    isOpen={isInvoiceModalOpen}
                    onClose={() => setIsInvoiceModalOpen(false)}
                    accountId={String(invoiceAccount.AccountID)}
                />
            )}
        </div>
    );
}
